class RoomFilter extends HTMLElement {
  itemsPerRoom = 6;
  visibleCounts = {};

  settings = {
    selectors: {
      image: '.inspiration-image',
      filterInput: 'input[name="room-filter"]',
      loadMoreBtn: '.load-more-btn',
      drawer: "[data-trigger-id='products-in-this-image_DrawerTrigger']",
      drawerBody: '.drawer__body',
      hotspot: '.inspiration-hotspots',
      grid: '[data-inspiration-static-grid]'
    }
  };

  constructor() {
    super();

    this.loadMoreBtn = this.querySelector(this.settings.selectors.loadMoreBtn);

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.loadMoreBtn?.addEventListener('click', this.onLoadMore);

    this.initialise();
  }

  normaliseRoom = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/\s+/g, '-');

  getCheckedRoom = () => {
    const input = this.querySelector(`${this.settings.selectors.filterInput}:checked`);
    return this.normaliseRoom(input?.dataset.roomKey || input?.value || 'all');
  };

  getRoomKeys = () => {
    return [...new Set(
      Array.from(this.querySelectorAll(this.settings.selectors.filterInput))
        .map((input) => this.normaliseRoom(input.dataset.roomKey || input.value))
        .filter((room) => room && room !== 'all')
    )];
  };

  getRoomImages = (room) => {
    const key = this.normaliseRoom(room);
    const images = Array.from(this.querySelectorAll(this.settings.selectors.image));

    if (key === 'all') return images;

    return images.filter((image) => this.normaliseRoom(image.dataset.roomKey) === key);
  };

  getDisplayOrder = (image) => {
    const order = Number.parseInt(image.dataset.displayOrder, 10);
    return Number.isFinite(order) ? order : Number.MAX_SAFE_INTEGER;
  };

  sortImagesByDisplayOrder() {
    const grid = this.querySelector(this.settings.selectors.grid);
    if (!grid) return;

    const images = Array.from(grid.querySelectorAll(this.settings.selectors.image));

    images
      .sort((firstImage, secondImage) => {
        const orderDifference = this.getDisplayOrder(firstImage) - this.getDisplayOrder(secondImage);

        if (orderDifference !== 0) return orderDifference;

        const firstIndex = Number.parseInt(firstImage.dataset.sourceIndex, 10) || 0;
        const secondIndex = Number.parseInt(secondImage.dataset.sourceIndex, 10) || 0;
        return firstIndex - secondIndex;
      })
      .forEach((image) => grid.appendChild(image));
  }

  initialise() {
    this.sortImagesByDisplayOrder();
    this.visibleCounts.all = this.itemsPerRoom;

    this.getRoomKeys().forEach((room) => {
      this.visibleCounts[room] = this.itemsPerRoom;
    });

    requestAnimationFrame(() => this.applyVisibility());
  }

  onChange = ({ target }) => {
    if (target.name !== 'room-filter') return;

    const room = this.normaliseRoom(target.dataset.roomKey || target.value);

    if (room !== 'all' && this.visibleCounts[room] == null) {
      this.visibleCounts[room] = this.itemsPerRoom;
    }

    this.applyVisibility();
  };

  applyVisibility() {
    const activeRoom = this.getCheckedRoom();
    const images = this.getRoomImages(activeRoom);
    const visibleCount = this.visibleCounts[activeRoom] || this.itemsPerRoom;

    this.querySelectorAll(this.settings.selectors.image).forEach((image) => {
      image.classList.add('hidden');
    });

    images
      .slice(0, visibleCount)
      .forEach((image) => image.classList.remove('hidden'));

    this.updateLoadMore();
  }

  updateLoadMore() {
    if (!this.loadMoreBtn) return;

    const activeRoom = this.getCheckedRoom();

    const remaining = Math.max(
      0,
      this.getRoomImages(activeRoom).length - (this.visibleCounts[activeRoom] || this.itemsPerRoom)
    );

    if (remaining > 0) {
      const text = this.loadMoreBtn.querySelector('.load-more-text');
      if (text) text.textContent = `Показать ещё ${Math.min(this.itemsPerRoom, remaining)}`;
      this.loadMoreBtn.classList.remove('hidden');
    } else {
      this.loadMoreBtn.classList.add('hidden');
    }
  }

  onLoadMore = () => {
    const activeRoom = this.getCheckedRoom();
    const total = this.getRoomImages(activeRoom).length;
    const current = this.visibleCounts[activeRoom] || this.itemsPerRoom;

    this.visibleCounts[activeRoom] = Math.min(total, current + this.itemsPerRoom);

    this.applyVisibility();
  };

  onKeydown = (event) => {
    if (event.key !== 'Enter') return;

    const target = event.target;
    if (target.classList.contains('button-room-filter')) {
      target.previousElementSibling?.click();
    }
  };

  onClick = (event) => {
    if (window.innerWidth >= 750) return;

    const image = event.target.closest(`${this.settings.selectors.image}.hotspotenabled`);
    if (!image) return;

    const hotspot = image.querySelector(this.settings.selectors.hotspot);
    const drawerBody = document
      .querySelector(this.settings.selectors.drawer)
      ?.querySelector(this.settings.selectors.drawerBody);

    if (hotspot && drawerBody) {
      drawerBody.closest('drawer-component')?.classList.add('active');
      drawerBody.replaceChildren(hotspot.cloneNode(true));
    }
  };
}

if (!customElements.get('room-filter')) {
  customElements.define('room-filter', RoomFilter);
}
